// MACHINERY V2 (Stroje) — V4 shop-floor wall: one machine tile (M2.14).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.14 + desktop/README.md §V4.
// Reference impl: desktop/machinery-v4-wall.jsx (V4Tile). Rendered truth:
// desktop/screenshots/v4-wall--refined-claude.png and
// v4-wall--machineid-native.png (both viewed before this file was written, per
// the view-before-code rule).
//
// Anatomy, top to bottom, as the artboard draws it:
//   large StatusBadge (or the mono NEPRIPOJENY mark) · hall, mono, right · conn
//   dot → model 28/700/-0.02em + mono "brand · SN" → spacer → the
//   status-specific body (wallTileBody.tsx).
//
// >>> IT IS A TV. NOTHING HERE IS INTERACTIVE. <<< No onClick, no onKeyDown, no
// tabIndex, no role, no hover style, no cursor, no kebab, no capability badges,
// no NEW badge. That is not an omission — the surface is meant to be opened
// fullscreen on a hall screen and left there, and a focusable MACHINE TILE on
// an unattended display is a trap, not an affordance. It is also why the
// nested-interactive stopPropagation discipline that M2.13 needed does not
// arise here: there is no outer click handler for a child to escape.
//
// THIS IS THE DATA SURFACE, AND THE RULE HOLDS HERE ABSOLUTELY. Owner ruling
// 2026-08-24 put exactly ONE interactive control on the wall — the exit X in
// the header (WallExitButton.tsx) — and thereby narrowed the ticket's blanket
// "NO interactions" to "no interactions on the DATA surface". None of that
// reaches this file: a tile still renders no control of any kind, and the X is
// chrome about the page, not about a machine.
//
// STATUS COMES FROM deriveCardPresentation(), ONCE, IN THE PARENT. This tile
// receives the presentation object and reads its flags; it never re-derives a
// state and never re-tests raw telemetry.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { getBrandDisplayName, getMachineDisplayName } from "../../../../../shared/utils/machineUtils";
import { Machine } from "../../../models/models";
import ConnDot from "../ConnDot";
import StatusBadge from "../StatusBadge";
import { MachineryV2CardPresentation, MachineryV2Telemetry } from "../cardStates";
import WallTileBody from "./wallTileBody";
import { wallTileStyle } from "./wallTileStyles";

/** The mono mark that replaces the badge when there is no status to paint. */
const MonoMark: React.FC<{ text: string }> = ({ text }) => (
  <span
    style={{
      fontFamily: "var(--font-mono)",
      fontSize: 13,
      color: "var(--faint)",
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      fontWeight: 600,
      whiteSpace: "nowrap",
    }}
  >
    {text}
  </span>
);

export interface WallTileProps {
  machine: Machine;
  presentation: MachineryV2CardPresentation;
  telemetry: MachineryV2Telemetry;
}

const WallTile: React.FC<WallTileProps> = ({ machine, presentation: p, telemetry: t }) => {
  const { translate } = useTranslation("myMachinery");

  // The artboard's "HALA A" / "VYROBNA": the most specific location level we
  // hold. When a machine has a parent location, `locationName` IS the hall;
  // when it has none, that single level is the best label there is. ABSENT =
  // RENDER NOTHING, NEVER A PLACEHOLDER — the rule this file follows for every
  // optional field, `meta` below included.
  const hall = machine.locationName || null;

  // The big line is the MODEL alone (artboard: "DMC 100 U duoBLOCK" with
  // "DMG MORI · SN SN-30886" beneath it), falling back to the shared display
  // name for custom machines that carry no model string.
  const model = machine.model || getMachineDisplayName(machine);
  const brand = getBrandDisplayName(machine);
  // THE SN CLAUSE IS IN THE FILTER CHAIN, not bypassing it. It used to print
  // "SN —" for a machine with no serial, seven lines under this file's own rule
  // ("Absent = render nothing, never a placeholder") — so a wall of freshly
  // imported catalog machines showed a column of "DMG MORI · SN —". The brand
  // half was already honest; both halves are now.
  const meta = [
    brand || null,
    machine.serialNumber ? "SN " + machine.serialNumber : null,
  ]
    .filter(Boolean)
    .join(" · ");

  /**
   * THE HEADER MARK — the same three-way choice StatusCell makes in the V3
   * table (fleetTableCells.tsx:98, read at staging time), for the same reason.
   * `showStatusRow` alone does not promise a visible badge: a `stale` machine
   * has it TRUE but carries no status ("TelemetryStale" maps to
   * `{ conn: "stale", status: null }`, fleetTelemetry.ts:100), so StatusBadge
   * would return null and the tile would head a 280px block with empty space.
   * On a wall that reads as a broken screen, so the mono state name stands in.
   */
  const mark = !p.showStatusRow ? (
    <MonoMark text={translate("myMachinery:v2.states.unconnected")} />
  ) : p.showAwaitingBadge ? (
    <MonoMark text={translate("myMachinery:v2.states.awaiting")} />
  ) : p.badgeStatus === null ? (
    <MonoMark text={translate("myMachinery:v2.states.stale")} />
  ) : (
    <StatusBadge status={p.badgeStatus} stale={p.staleLook} variant="large" />
  );

  return (
    <article style={wallTileStyle(p)}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
        {mark}
        <span style={{ flex: 1, minWidth: 0 }} />
        {hall && (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 13,
              color: "var(--ink-2)",
              opacity: 0.7,
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 160,
            }}
          >
            {hall}
          </span>
        )}
        {/* The artboard PINS this dot absolutely at top-right, where it lands on
            top of the hall label's last glyph. In flow at the end of the same
            row it sits in the same corner and cannot collide — the only change
            is that it is vertically centred on the label instead of 4px above
            it. `dotConn` (never raw `conn`) decides whether it renders at all:
            unconnected and awaiting machines show no dot, per §4.3. */}
        <ConnDot state={p.dotConn} lastHeartbeat={t.heartbeat} />
      </div>

      <div style={{ marginTop: 2, minWidth: 0 }}>
        <div
          style={{
            fontSize: 28,
            lineHeight: 1.05,
            fontWeight: 700,
            color: "var(--ink)",
            letterSpacing: "-0.02em",
            // The tile clips, so a long model must WRAP rather than be cut off:
            // a half-visible machine name on a hall screen is worse than two
            // lines. `gridAutoRows: minmax(280px, auto)` on the wall lets the
            // row grow to take it.
            overflowWrap: "anywhere",
          }}
        >
          {model}
        </div>
        {meta && (
          <div
            style={{
              fontSize: 13,
              color: "var(--ink-2)",
              opacity: 0.7,
              marginTop: 4,
              fontFamily: "var(--font-mono)",
              letterSpacing: "0.04em",
              overflowWrap: "anywhere",
            }}
          >
            {meta}
          </div>
        )}
      </div>

      <span style={{ flex: 1 }} />

      <WallTileBody
        presentation={p}
        telemetry={t}
        openSr={machine.openServiceReportsCount || 0}
      />
    </article>
  );
};

export default WallTile;
